import React, { useEffect, useState } from 'react';
import { Map, X } from 'lucide-react';
import { NODE_STYLES } from './constants';

export function PipelineMiniMap({ steps, selectedNode, onJump, canvasRef, scrollTick }) {
  const [collapsed, setCollapsed] = useState(false);
  const [viewport, setViewport] = useState({ top: 0, height: 100, total: 100 });

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => {
      const total = Math.max(el.scrollHeight, 1);
      setViewport({ top: el.scrollTop, height: el.clientHeight, total });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [canvasRef, steps.length, scrollTick]);

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="absolute bottom-4 right-4 z-20 p-2 rounded-lg bg-background/90 backdrop-blur-md border border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all shadow-lg"
        title="Show minimap"
        data-testid="minimap-expand"
      >
        <Map className="w-4 h-4" />
      </button>
    );
  }

  const MINIMAP_H = 220;
  const MINIMAP_W = 150;
  const total = Math.max(viewport.total, 1);
  const vpTop = (viewport.top / total) * MINIMAP_H;
  const vpH = Math.max((viewport.height / total) * MINIMAP_H, 12);

  return (
    <div
      className="absolute bottom-4 right-4 z-20 rounded-xl bg-background/90 backdrop-blur-md border border-border/50 shadow-lg overflow-hidden"
      style={{ width: MINIMAP_W + 2 }}
      data-testid="pipeline-minimap"
    >
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border/40 bg-muted/20">
        <div className="flex items-center gap-1.5">
          <Map className="w-3 h-3 text-primary" />
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Overview</span>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          className="p-0.5 rounded hover:bg-muted/50 text-muted-foreground/80 hover:text-foreground"
          title="Hide minimap"
          data-testid="minimap-collapse"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="relative p-2" style={{ height: MINIMAP_H }}>
        <div
          className="absolute left-1 right-1 rounded-md border border-primary/60 bg-primary/10 pointer-events-none transition-all"
          style={{ top: 8 + vpTop, height: vpH }}
        />
        <div className="relative flex flex-col items-center gap-1 h-full">
          {steps.map((step, idx) => {
            const style = NODE_STYLES[step.type] || NODE_STYLES.source;
            const isSel = selectedNode === idx;
            return (
              <React.Fragment key={idx}>
                <button
                  onClick={() => onJump(idx)}
                  className={`w-full h-4 rounded flex items-center gap-1.5 px-1.5 transition-all border ${
                    isSel
                      ? `${style.border} ${style.bg} ring-1 ${style.ring}`
                      : 'border-border/30 bg-background/60 hover:border-primary/40'
                  }`}
                  title={`${idx + 1}. ${step.type.replace('_', ' ')}`}
                  data-testid={`minimap-node-${idx}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot} flex-shrink-0`} />
                  <span className="text-[8px] text-muted-foreground/80 capitalize truncate leading-none">{step.type.replace('_', ' ')}</span>
                </button>
                {idx < steps.length - 1 && <span className="w-px h-1 bg-border/40" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
